import { useNavigate, useSearch } from '@tanstack/react-router';
import { Download, FileSpreadsheet, FileText, Plus } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import type { HOURS_FORMATS, Report, ReportFilters } from '@tt/shared';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Badge, Spinner } from '@/components/ui/misc';
import { NativeSelect } from '@/components/ui/native-select';
import { downloadReport, useDeleteReport, useReports, useRequestReport } from '@/lib/admin-queries';
import { describeError } from '@/lib/errors';
import { formatDate } from '@/lib/format';
import { useFormat } from '@/lib/use-format';
import { AdminPage, ConfirmDialog, DataTable, Pagination } from './admin-ui';

const TYPE_ICON = {
  PDF_SUMMARY: FileText,
  XLSX_ENTRIES: FileSpreadsheet,
  XLSX_DATABASE: FileSpreadsheet,
} as const;

/** Report history: what was asked for, how it went, and the download while it lasts. */
export function AdminReportsPage() {
  const { t } = useTranslation();
  const fmt = useFormat();
  const navigate = useNavigate();
  const search = useSearch({ from: '/app/admin/reports' });
  const page = search.page ?? 1;
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState<Report | null>(null);

  const reports = useReports({ page, pageSize: 20 });
  const remove = useDeleteReport();
  const request = useRequestReport();

  const run = (promise: Promise<unknown>, message: string) =>
    promise.then(
      () => toast.success(message),
      (error: unknown) => toast.error(describeError(error, t)),
    );

  return (
    <AdminPage
      title={t('admin.reports.title')}
      description={t('admin.reports.subtitle')}
      actions={
        <>
          <Button
            variant="outline"
            onClick={() =>
              void run(request.mutateAsync({ kind: 'database' }), t('admin.reports.queued'))
            }
          >
            <FileSpreadsheet className="h-4 w-4" aria-hidden="true" />{' '}
            {t('admin.reports.exportDatabase')}
          </Button>
          <Button onClick={() => setCreating(true)}>
            <Plus className="h-4 w-4" aria-hidden="true" /> {t('admin.reports.new')}
          </Button>
        </>
      }
    >
      <DataTable
        caption={t('admin.reports.title')}
        isPending={reports.isPending}
        error={reports.isError ? describeError(reports.error, t) : undefined}
        empty={t('admin.reports.none')}
        rows={reports.data?.data ?? []}
        rowKey={(r) => r.id}
        footer={
          <Pagination
            page={page}
            totalPages={reports.data?.meta.totalPages ?? 1}
            total={reports.data?.meta.total ?? 0}
            onChange={(next) =>
              void navigate({ to: '/admin/reports', search: { ...search, page: next } })
            }
          />
        }
        columns={[
          {
            key: 'title',
            header: t('admin.reports.report'),
            cell: (r) => {
              const Icon = TYPE_ICON[r.type];
              return (
                <span className="inline-flex items-start gap-2">
                  <Icon
                    className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground"
                    aria-hidden="true"
                  />
                  <span>
                    <span className="block font-medium">{r.title}</span>
                    <span className="block text-xs text-muted-foreground">
                      {t(`admin.reports.type.${r.type}`)}
                      {r.rowCount !== null
                        ? ` · ${t('admin.reports.rows', { count: r.rowCount })}`
                        : ''}
                    </span>
                  </span>
                </span>
              );
            },
          },
          {
            key: 'status',
            header: t('admin.reports.status'),
            cell: (r) => <Status report={r} />,
          },
          {
            key: 'requestedBy',
            header: t('admin.reports.requestedBy'),
            cell: (r) => r.requestedBy?.name ?? '—',
          },
          {
            key: 'created',
            header: t('admin.reports.created'),
            cell: (r) => formatDate(r.createdAt.slice(0, 10), fmt),
          },
          {
            key: 'size',
            header: t('admin.reports.size'),
            numeric: true,
            cell: (r) => (r.fileSizeBytes ? formatBytes(r.fileSizeBytes) : '—'),
          },
          {
            key: 'actions',
            header: <span className="sr-only">{t('admin.actions')}</span>,
            numeric: true,
            cell: (r) => (
              <div className="flex justify-end gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={r.status !== 'COMPLETED'}
                  onClick={() => downloadReport(r.id)}
                  aria-label={t('admin.reports.download', { title: r.title })}
                >
                  <Download className="h-4 w-4" aria-hidden="true" />
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setDeleting(r)}>
                  {t('app.delete')}
                </Button>
              </div>
            ),
          },
        ]}
      />

      {creating ? <NewReportDialog onClose={() => setCreating(false)} /> : null}

      <ConfirmDialog
        open={Boolean(deleting)}
        onOpenChange={(open) => !open && setDeleting(null)}
        title={t('admin.reports.deleteTitle', { title: deleting?.title ?? '' })}
        description={t('admin.reports.deleteBody')}
        confirmLabel={t('app.delete')}
        onConfirm={() => {
          if (!deleting) return;
          void run(remove.mutateAsync(deleting.id), t('admin.reports.deleted'));
        }}
      />
    </AdminPage>
  );
}

function Status({ report }: { report: Report }) {
  const { t } = useTranslation();
  const pending = report.status === 'QUEUED' || report.status === 'RUNNING';
  return (
    <span className="inline-flex items-center gap-2">
      {pending ? <Spinner className="h-3.5 w-3.5" /> : null}
      <Badge>{t(`admin.reports.statusLabel.${report.status}`)}</Badge>
      {report.status === 'FAILED' && report.errorMessage ? (
        <span className="text-xs text-destructive">{report.errorMessage}</span>
      ) : null}
    </span>
  );
}

/**
 * Pre-filled from the organization dashboard when it opens this, so "what I am looking at" and
 * "what I am exporting" are the same filters.
 */
export function NewReportDialog({
  onClose,
  initialFilters,
}: {
  onClose: () => void;
  initialFilters?: Partial<ReportFilters>;
}) {
  const { t } = useTranslation();
  const request = useRequestReport();
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    kind: 'pdf' as 'pdf' | 'entries',
    title: '',
    from: initialFilters?.from ?? today.slice(0, 8) + '01',
    to: initialFilters?.to ?? today,
    hoursFormat: '' as '' | (typeof HOURS_FORMATS)[number],
    includeCharts: true,
  });
  const [error, setError] = useState<unknown>(null);

  const submit = async () => {
    setError(null);
    const filters: ReportFilters = {
      from: form.from,
      to: form.to,
      userIds: initialFilters?.userIds,
      groupIds: initialFilters?.groupIds,
      projectIds: initialFilters?.projectIds,
      activityIds: initialFilters?.activityIds,
    };
    try {
      await request.mutateAsync({
        kind: form.kind,
        title: form.title || undefined,
        filters,
        ...(form.kind === 'pdf'
          ? {
              options: {
                includeCharts: form.includeCharts,
                ...(form.hoursFormat ? { hoursFormat: form.hoursFormat } : {}),
              },
            }
          : {}),
      });
      toast.success(t('admin.reports.queued'));
      onClose();
    } catch (e) {
      setError(e);
    }
  };

  const scoped = [
    initialFilters?.userIds?.length && t('admin.org.users'),
    initialFilters?.groupIds?.length && t('admin.org.groups'),
    initialFilters?.projectIds?.length && t('admin.org.projects'),
    initialFilters?.activityIds?.length && t('admin.org.activities'),
  ].filter(Boolean) as string[];

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('admin.reports.new')}</DialogTitle>
        </DialogHeader>
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
        >
          <Field label={t('admin.reports.kind')}>
            {(props) => (
              <NativeSelect
                {...props}
                className="w-full"
                value={form.kind}
                onChange={(e) =>
                  setForm((f) => ({ ...f, kind: e.target.value as 'pdf' | 'entries' }))
                }
              >
                <option value="pdf">{t('admin.reports.type.PDF_SUMMARY')}</option>
                <option value="entries">{t('admin.reports.type.XLSX_ENTRIES')}</option>
              </NativeSelect>
            )}
          </Field>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label={t('admin.org.from')} required>
              {(props) => (
                <Input
                  {...props}
                  type="date"
                  value={form.from}
                  onChange={(e) => setForm((f) => ({ ...f, from: e.target.value }))}
                  required
                />
              )}
            </Field>
            <Field label={t('admin.org.to')} required>
              {(props) => (
                <Input
                  {...props}
                  type="date"
                  value={form.to}
                  onChange={(e) => setForm((f) => ({ ...f, to: e.target.value }))}
                  required
                />
              )}
            </Field>
          </div>
          <Field label={t('admin.reports.titleField')}>
            {(props) => (
              <Input
                {...props}
                value={form.title}
                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                placeholder={t('admin.reports.titlePlaceholder')}
              />
            )}
          </Field>
          {form.kind === 'pdf' ? (
            <>
              <Field label={t('admin.settings.hoursFormat')}>
                {(props) => (
                  <NativeSelect
                    {...props}
                    className="w-full"
                    value={form.hoursFormat}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        hoursFormat: e.target.value as '' | (typeof HOURS_FORMATS)[number],
                      }))
                    }
                  >
                    <option value="">{t('admin.reports.orgDefault')}</option>
                    <option value="DECIMAL">{t('admin.settings.hoursDecimal')}</option>
                    <option value="HH_MM">{t('admin.settings.hoursHm')}</option>
                  </NativeSelect>
                )}
              </Field>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  className="h-4 w-4"
                  checked={form.includeCharts}
                  onChange={(e) => setForm((f) => ({ ...f, includeCharts: e.target.checked }))}
                />
                {t('admin.reports.includeCharts')}
              </label>
            </>
          ) : null}

          {scoped.length ? (
            <p className="text-xs text-muted-foreground">
              {t('admin.reports.scoped', { filters: scoped.join(', ') })}
            </p>
          ) : null}
          {error ? (
            <p className="text-sm text-destructive" role="alert">
              {describeError(error, t)}
            </p>
          ) : null}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              {t('app.cancel')}
            </Button>
            <Button type="submit" disabled={request.isPending}>
              {t('admin.reports.generate')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} kB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}
